import Slider from '$tablet-app/src/app/components/Slider';
import { palette } from '$tablet-app/src/app/theme';
import { colors, spacing } from '@akin-home/theme';
import { showErrorToast } from '@akin-home/toast/native';
import React from 'react';
import { StyleSheet, View, ViewStyle } from 'react-native';
import { useGetRobotVolume, useSetRobotVolume } from '../../audio/hooks';
import { RobotSettings, useRobotSetting } from '../../info';
import { RobotDevice } from '../../types';
import {
  RobotSettingsHeader,
  RobotSettingsHeaderProps,
} from './RobotSettingsHeader';

type Props = {
  robot: RobotDevice | undefined;
  soundSettingName: keyof RobotSettings; // Name of robot setting that tracks whether sound is enabled or not
  style?: ViewStyle;
  headerProps?: RobotSettingsHeaderProps;
  disabled?: boolean;
  onChange?: (value: number) => void;
};

export const RobotSettingsVolumeSlider = (props: Props) => {
  const { robot } = props;

  const {
    query: { data: isSoundEnabled },
  } = useRobotSetting(robot, props.soundSettingName);

  const { data: robotVolume } = useGetRobotVolume(robot);
  const updateRobotVolume = useSetRobotVolume(robot, {
    onError: () => {
      showErrorToast({
        text1: 'Error while Updating Robot Volume',
        text2: 'Please try again',
      });
    },
  });

  const handleVolumeUpdate = async (newVolume: number) => {
    if (!robot?.id) return;

    try {
      await updateRobotVolume.mutateAsync(newVolume);
    } catch (err) {
      console.error('Error in updating robot volume', err);
    }
  };

  const disabled = (props.disabled || !isSoundEnabled) ?? false;

  return (
    <View style={[styles.container, props.style]}>
      <RobotSettingsHeader
        disabled={disabled}
        {...(props?.headerProps ?? {})}
      />

      <Slider
        style={{ flex: 1 }}
        value={robotVolume}
        onSlidingComplete={handleVolumeUpdate}
        minimumValue={0}
        maximumValue={1}
        disabled={disabled}
        minimumTrackTintColor={disabled ? colors.grey.dim : palette.primary_70}
        maximumTrackTintColor={disabled ? colors.grey.dim : 'grey'}
        thumbStyle={[
          styles.thumbStyle,
          disabled && { borderColor: colors.grey.dim },
        ]}
      />

      {/* We need to add an overleay view when it is disabled.
      This is because the disabled prop doesnt actually block interactions with the slider. */}
      {disabled ? <View style={styles.disabledOverlay} /> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: spacing[5],
    width: '30%'
  },
  thumbStyle: {
    width: 30,
    height: 30,
    borderRadius: 15,
    borderWidth: 4,
    borderColor: palette.primary_70,
  },
  disabledOverlay: {
    width: '100%',
    height: '100%',
    position: 'absolute',
  },
});
